import Delta from 'quill-delta';
import * as clientEnv from 'libs/client-env';
import { fastCompose } from 'libs/quill-utils';
export const getContrastColor = (hexColor) => {
    if (hexColor === '') {
        // NOTE: Do not change the color at all. CSS styling property will be removed.
        return '';
    }
    if (hexColor === 'default' || hexColor === 'var(--keyword-bgcolor)') {
        // NOTE: Hex color has the value default for keywords without any categories.
        return 'black';
    }
    // NOTE: Based on the background color (function parameter) the text color will be calculated.
    // The text color will be set to black or white. The breaking point is calculated based on the
    // formula used in wordspotting library:
    //  https://github.com/spectreconsole/spectre.console/blob/e66d3aab2ea41afe2e31f7ae1fb5fe1e77821455/examples/Shared/Extensions/ColorExtensions.cs#L11
    const red = parseInt(hexColor.substring(1, 2), 16);
    const green = parseInt(hexColor.substring(3, 2), 16);
    const blue = parseInt(hexColor.substring(5, 2), 16);
    const luminance = red * 0.2126 + green * 0.7152 + blue * 0.0722;
    return luminance < 140 ? 'white' : 'black';
};
export const filterKeywordsOnly = (textList) => {
    return textList.flatMap((innerList) => innerList
        .filter((keyword) => keyword.highlight !== undefined && keyword.timestampSec !== undefined));
};
export default class Keywords {
    constructor(editorController) {
        this.highlightedKeyword = null;
        this.previousKeywords = [];
        this.highlightAllKeywords = (keywordHighlight) => {
            if (!this.editorController.session.login.hasClaim('keywords:enabled')) {
                return;
            }
            if (clientEnv.getShowKeywords() === false) {
                return;
            }
            const newKeywords = filterKeywordsOnly(keywordHighlight);
            const removedKeywords = this.previousKeywords.filter((prevKeyword) => {
                return !newKeywords.some((currKeyword) => prevKeyword.text === currKeyword.text
                    && prevKeyword.timestampSec === currKeyword.timestampSec
                    && prevKeyword.type === currKeyword.type);
            });
            let delta = new Delta();
            const keywordActions = [
                ...removedKeywords.map((keyword) => ({ keyword, action: 'remove' })),
                ...newKeywords.map((keyword) => ({ keyword, action: 'add' })),
            ];
            keywordActions.forEach(({ keyword, action }) => {
                var _a;
                const { from } = this.editorController.textMetadata.getTimestampsAtTime(keyword.timestampSec);
                delta = fastCompose(delta, (_a = this.buildKeywordDelta(keyword, from, action)) !== null && _a !== void 0 ? _a : delta);
            });
            this.editorController.execTextChange({ runAligner: false, requestSave: false }, () => {
                this.editorController.updateContents(delta, 'user');
            });
            this.previousKeywords = newKeywords;
        };
        this.highlightActiveKeyword = (activeKeyword) => {
            const { from: nextFrom } = this.editorController.textMetadata
                .getTimestampsAtTime(activeKeyword.timestamp);
            this.editorController.execTextChange({ runAligner: false, requestSave: false }, () => {
                if (this.highlightedKeyword !== null) {
                    const { from: previousFrom } = this.editorController.textMetadata
                        .getTimestampsAtTime(this.highlightedKeyword.timestamp);
                    this.editorController.formatText(this.getKeywordStart(this.highlightedKeyword, previousFrom), this.highlightedKeyword.text.length, { keyword: `result ${this.highlightedKeyword.highlight} ${getContrastColor(this.highlightedKeyword.highlight)}` }, 'api');
                }
                this.editorController.formatText(this.getKeywordStart(activeKeyword, nextFrom), activeKeyword.text.length, { keyword: `highlighted ${activeKeyword.highlight} ${getContrastColor(activeKeyword.highlight)}` }, 'api');
            });
            this.editorController.playback.seekTo(activeKeyword.timestamp);
            this.highlightedKeyword = activeKeyword;
        };
        this.toneDownKeyword = (timeStamp, keyword) => {
            const { from } = this.editorController.textMetadata.getTimestampsAtTime(timeStamp);
            this.editorController.execTextChange({ runAligner: false, requestSave: false }, () => {
                if (keyword.highlight === undefined)
                    return;
                this.editorController.formatText(this.getWordStartAfterSpeaker(from), keyword.text.length, { keyword: `result ${keyword.highlight} ${getContrastColor(keyword.highlight)}` }, 'api');
            });
        };
        this.buildKeywordDelta = (keyword, from, type) => {
            const wordStart = this.getKeywordStart(keyword, from);
            if (this.isKeywordInEditor(keyword, wordStart) && keyword.highlight !== undefined) {
                const highlightFormat = `result ${keyword.highlight} ${getContrastColor(keyword.highlight)}`;
                return new Delta()
                    .retain(wordStart)
                    .retain(keyword.text.length, { keyword: type === 'add' ? highlightFormat : false });
            }
            return null;
        };
        this.isKeywordInEditor = (keyword, from) => {
            const lowerCaseKeywordText = keyword.text.toLowerCase().trim();
            const textInEditor = this.editorController.getText(from, keyword.text.length)
                .toLowerCase();
            if (textInEditor.includes(lowerCaseKeywordText)) {
                return true;
            }
            return false;
        };
        this.getKeywordStart = (keyword, from) => {
            if (keyword.type === 'phrase') {
                // NOTE: Speaker can share the same timestamp. We get the first following word if so.
                return this.getWordStartAfterSpeaker(from);
            }
            return this.getWordStartInNontranscriptFormat(keyword, from);
        };
        this.getWordStartAfterSpeaker = (from) => {
            const nextLineIndex = this.editorController.getNextNewLineIndex(from + 1);
            if (this.editorController.getLineFormat(from + 1).speaker !== undefined
                && nextLineIndex !== null) {
                return nextLineIndex + 1;
            }
            return from;
        };
        this.getWordStartInNontranscriptFormat = (keyword, from) => {
            var _a, _b;
            if (this.editorController.captions.enabled) {
                // NOTE: Captions have different timestamps,
                //  "from" is the first caption letter after nontranscript formats.
                return (_a = this.findWordStartInPrecedingNontranscriptLines(keyword, from)) !== null && _a !== void 0 ? _a : from;
            }
            const nextLineIndex = this.editorController.getNextNewLineIndex(from + 1);
            return (_b = this.findWordStartInLine(keyword.text, from, nextLineIndex)) !== null && _b !== void 0 ? _b : from;
        };
        this.findWordStartInPrecedingNontranscriptLines = (keyword, from) => {
            const precedingLineStarts = this.editorController.getBlockStarts(0, from - 1);
            for (let i = precedingLineStarts.length - 1; i >= 0; i -= 1) {
                const lineStartIndex = precedingLineStarts[i];
                const lineFormat = this.editorController.getLineFormat(lineStartIndex);
                if (this.editorController.isNonTranscriptFormat(lineFormat)) {
                    const wordStart = this.findWordStartInLine(keyword.text, lineStartIndex, from);
                    if (wordStart !== null) {
                        return wordStart;
                    }
                    continue;
                }
                else {
                    return null;
                }
            }
            return null;
        };
        this.findWordStartInLine = (keywordText, from, to) => {
            if (to === null) {
                return null;
            }
            const lineContent = this.editorController.getText(from, to - from);
            const wordStartPosition = lineContent.indexOf(keywordText);
            if (wordStartPosition !== -1) {
                return wordStartPosition + from;
            }
            return null;
        };
        this.editorController = editorController;
    }
}
